import React from 'react';
import { Hand, UserCheck, UserX, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';

/*
 * r16p (design 5.8, 6.3, 7.1 - R28, R19): a HELD reply waits for its author; the worker types nothing until the author chooses.
 *   new_inbound   newer messages from the other person arrived after the reply was written:
 *                 "New messages arrived since you wrote this. [Send anyway] [Cancel]"
 *   confirm_peer  the worker opened the thread only to prove who it is with (the inbox list shows display names, not usernames):
 *                 "Send to @x? (the worker read this username in the thread) [Confirm] [Cancel]"
 * Both buttons call the server (POST /uiapi/chats/replies/{id}/release, DELETE /uiapi/chats/replies/{id}); nothing is decided here.
 *
 * Review P3 (2026-10-24): Confirm IS the human identity check (R19), so it is never offered blind. The username comes from the reply
 * (confirmPeerUsername and its spellings) or else from the conversation the server answered (thread.peer.username - where the
 * server's own reply row keeps none: DmReply.confirm_peer is a Boolean). With no username at all, Confirm stays disabled and the
 * card says why; Cancel always works.
 */
export default function HeldReplyCard({ reply, peerUsername, onRelease, onCancel, busy }) {
  const confirm = reply.holdReason === 'confirm_peer';
  const peer = reply.confirmPeer || (typeof peerUsername === 'string' && peerUsername.trim() ? peerUsername.trim().replace(/^@/, '') : null);
  const blind = confirm && !peer;
  return (
    <div className="rounded-xl border border-warning/40 bg-warning/10 p-3 text-sm" role="status" data-testid={`held-${reply.id}`}
      data-reason={reply.holdReason || 'unknown'} data-blind={blind ? 'true' : undefined}>
      <p className="flex items-start gap-2 font-medium text-foreground">
        {confirm
          ? (blind ? <UserX className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden /> : <UserCheck className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />)
          : <Hand className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />}
        {confirm ? (
          peer ? (
            <span>Send to <span className="font-mono" data-testid={`held-peer-${reply.id}`}>@{peer}</span>? <span className="font-normal text-muted-foreground">(the worker read this username in the thread)</span></span>
          ) : (
            <span data-testid={`held-blind-${reply.id}`}>
              Who this conversation is with is not known yet, so it cannot be confirmed here.{' '}
              <span className="font-normal text-muted-foreground">Nothing is sent. Confirm becomes possible once the worker has read the username; you can cancel now.</span>
            </span>
          )
        ) : (
          <span>New messages arrived since you wrote this. <span className="font-normal text-muted-foreground">Read them first; nothing is sent until you choose.</span></span>
        )}
      </p>
      <div className="mt-2.5 flex flex-wrap gap-2">
        <Button size="sm" className="tap h-9 rounded-full bg-warning text-background hover:bg-warning/90" disabled={busy || blind}
          onClick={() => { if (!blind) onRelease(reply); }} data-testid={`held-release-${reply.id}`}
          title={blind ? 'The username is not known yet' : undefined}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
          {confirm ? 'Confirm' : 'Send anyway'}
        </Button>
        <Button size="sm" variant="outline" className="tap h-9 rounded-full" disabled={busy}
          onClick={() => onCancel(reply)} data-testid={`held-cancel-${reply.id}`}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
